import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';

function Profile() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const [postsRes, bookmarksRes] = await Promise.all([
          axios.get(`/api/users/${user.id}/posts`),
          axios.get('/api/bookmarks')
        ]);
        
        setPosts(postsRes.data);
        setBookmarks(bookmarksRes.data);
      } catch (error) {
        setError('Error fetching user data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p>Please sign in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{user.name}</h1>
              <p className="text-gray-600">{user.email || user.phoneNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Member since</p>
              <p className="font-medium">
                {new Date(user.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="mt-4">
            <p className="text-gray-600">
              Engagement Score: <span className="font-medium">{user.engagementScore}</span>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('posts')}
                className={`py-4 px-6 ${
                  activeTab === 'posts'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                My Posts ({posts.length})
              </button>
              <button
                onClick={() => setActiveTab('bookmarks')}
                className={`py-4 px-6 ${
                  activeTab === 'bookmarks'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Bookmarks ({bookmarks.length})
              </button>
            </nav>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : error ? (
          <div className="text-red-500 text-center py-8">{error}</div>
        ) : (
          <div className="grid gap-6">
            {activeTab === 'posts' ? (
              posts.length > 0 ? (
                posts.map(post => (
                  <div key={post._id} className="border rounded-lg p-4">
                    <Link to={`/post/${post._id}`} className="block hover:bg-gray-50">
                      <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                      <div className="flex items-center text-sm text-gray-500">
                        <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                        <span className="mx-2">•</span>
                        <span>{post.views} views</span>
                        <span className="mx-2">•</span>
                        <span>{post.comments.length} comments</span>
                      </div>
                    </Link>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">No posts yet</p>
              )
            ) : bookmarks.length > 0 ? (
              bookmarks.map(bookmark => (
                <div key={bookmark._id} className="border rounded-lg p-4">
                  <Link to={`/post/${bookmark._id}`} className="block hover:bg-gray-50">
                    <h2 className="text-xl font-semibold mb-2">{bookmark.title}</h2>
                    <p className="text-gray-600 mb-2">
                      {bookmark.content.substring(0, 150)}...
                    </p>
                    <div className="text-sm text-gray-500">
                      Bookmarked on {new Date(bookmark.createdAt).toLocaleDateString()}
                    </div>
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">No bookmarks yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile; 